import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaFileInvoiceDollar, FaChartLine, FaWarehouse, FaTruck, FaMoneyBillWave, FaArrowLeft } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const formatCurrency = (val) => {
  if (!val) return '0.00';
  return Number(val).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
};

const formatQty = (val) => {
  if (!val) return '0.000 MT';
  return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' MT';
};

const MasterReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const contractNo = queryParams.get('contract');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contractNo) {
      fetchMasterReport(contractNo);
    } else {
      setLoading(false);
      setError("Please search for a contract to view the Master Report.");
    }
  }, [contractNo]);

  const fetchMasterReport = async (contractNumber) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/report/master-report?purchase_contract_no=${encodeURIComponent(contractNumber)}`,
        { withCredentials: true }
      );
      if (response.data && response.data.contract) {
        setData(response.data);
      } else {
        setError(`Contract "${contractNumber}" not found.`);
        setData(null);
      }
    } catch (err) {
      console.error("Error fetching master report", err);
      setError("Failed to load master report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
        <p className="text-gray-600 font-medium">Loading 360° Contract View...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 flex items-center gap-2 hover:underline">
          <FaArrowLeft /> Back
        </button>
        <NoRecordFound message={error || "No data available"} subMessage="Try searching again." />
      </div>
    );
  }

  // --- Calculations ---
  const { contract, salesOrders, deliveryOrders, warehouseStockFlow, costBreakups } = data;

  // 1. Costs
  const purchaseMaterialCost = Number(contract.total_value_inr) || 0;
  const totalOverheads = costBreakups.reduce((sum, cb) => sum + Number(cb.amount_inr || 0), 0);
  const totalCost = purchaseMaterialCost + totalOverheads;

  // 2. Sales Revenue
  const totalRevenue = salesOrders.reduce((sum, so) => sum + (Number(so.quantity_mt) * Number(so.sale_price_per_mt)), 0);
  const totalSalesQty = salesOrders.reduce((sum, so) => sum + Number(so.quantity_mt), 0);

  // 3. Profit & Loss
  const netProfit = totalRevenue - totalCost;
  const isProfit = netProfit >= 0;

  // 4. Warehouse Stocks
  const inwardQty = warehouseStockFlow.filter(w => w.type === 'Inward').reduce((sum, w) => sum + Number(w.quantity), 0);
  const outwardQty = warehouseStockFlow.filter(w => w.type === 'Outward').reduce((sum, w) => sum + Number(w.quantity), 0);
  // Transfer In/Out cancels out globally for this contract, but inwardQty represents total received from supplier.
  
  return (
    <div className="w-full mx-auto p-4 sm:p-6 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <button onClick={() => navigate(-1)} className="mb-2 text-sm text-gray-500 flex items-center gap-2 hover:text-blue-600 transition-colors">
            <FaArrowLeft /> Back
          </button>
          <h1 className="text-2xl font-extrabold text-gray-800 flex items-center gap-3">
            <FaFileInvoiceDollar className="text-blue-600" /> 
            Master Report: {contract.contract_no}
          </h1>
          <p className="text-gray-500 text-sm mt-1">Full lifecycle view from Purchase to Sales</p>
        </div>
        <div className={`px-4 py-2 rounded-full font-bold text-sm border ${contract.status === 'Open' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-300'}`}>
          Status: {contract.status}
        </div>
      </div>

      {/* P&L Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 text-gray-500 mb-2">
            <FaMoneyBillWave className="text-red-500 text-lg" />
            <h3 className="font-semibold text-sm">Total Cost (Purch + Exp)</h3>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(totalCost)}</p>
          <p className="text-xs text-gray-400 mt-1">Material: {formatCurrency(purchaseMaterialCost)}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 text-gray-500 mb-2">
            <FaChartLine className="text-blue-500 text-lg" />
            <h3 className="font-semibold text-sm">Total Sales Revenue</h3>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
          <p className="text-xs text-gray-400 mt-1">Sold Qty: {formatQty(totalSalesQty)}</p>
        </div>

        <div className={`rounded-xl shadow-sm border p-5 ${isProfit ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className={`flex items-center gap-3 mb-2 ${isProfit ? 'text-green-700' : 'text-red-700'}`}>
            <FaFileInvoiceDollar className="text-lg" />
            <h3 className="font-semibold text-sm">Net Profit / Loss</h3>
          </div>
          <p className={`text-2xl font-bold ${isProfit ? 'text-green-700' : 'text-red-700'}`}>
            {isProfit ? '+' : ''}{formatCurrency(netProfit)}
          </p>
          <p className={`text-xs mt-1 ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
            {isProfit ? 'Profitable Contract' : 'Loss Making Contract'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 text-gray-500 mb-2">
            <FaWarehouse className="text-purple-500 text-lg" />
            <h3 className="font-semibold text-sm">Godown Status</h3>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatQty(inwardQty - outwardQty)}</p>
          <p className="text-xs text-gray-400 mt-1">Unsold/Pending Stock</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Purchase & Costs */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-semibold text-gray-700">
              Purchase Details
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Supplier:</span> <span className="font-medium text-right">{contract.supplier}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Commodity:</span> <span className="font-medium text-right">{contract.material} ({contract.origin})</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Contract Date:</span> <span className="font-medium text-right">{contract.contract_date}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Vessel/Batch:</span> <span className="font-medium text-right">{contract.vessel_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Order Qty:</span> <span className="font-medium text-right">{formatQty(contract.total_qty_mt)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Unit Price:</span> <span className="font-medium text-right">{formatCurrency(contract.unit_price_inr)}</span></div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-semibold text-gray-700">
              Cost Breakup (Overheads)
            </div>
            <div className="p-0">
              {costBreakups.length > 0 ? (
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b">
                    <tr><th className="px-4 py-2">Component</th><th className="px-4 py-2 text-right">Amount</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {costBreakups.map((cb, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-700">{cb.component_code}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(cb.amount_inr)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-bold text-gray-900">
                      <td className="px-4 py-2">Total Extra Costs</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(totalOverheads)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div className="p-4 text-center text-sm text-gray-500">No overhead costs recorded.</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Sales, Delivery, Warehouse */}
        <div className="lg:col-span-2 space-y-6">
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 flex items-center gap-2">
              <FaChartLine className="text-blue-500" /> Sales Orders
            </div>
            <div className="overflow-x-auto">
              {salesOrders.length > 0 ? (
                <table className="w-full text-sm text-left whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b">
                    <tr>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Buyer</th>
                      <th className="px-4 py-2">Godown</th>
                      <th className="px-4 py-2 text-right">Qty (MT)</th>
                      <th className="px-4 py-2 text-right">Rate</th>
                      <th className="px-4 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {salesOrders.map((so, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{so.order_date}</td>
                        <td className="px-4 py-2 font-medium text-gray-800">{so.buyer_name}</td>
                        <td className="px-4 py-2 text-gray-600">{so.warehouse_name || '-'}</td>
                        <td className="px-4 py-2 text-right">{formatQty(so.quantity_mt)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(so.sale_price_per_mt)}</td>
                        <td className="px-4 py-2 text-right font-semibold text-green-700">
                          {formatCurrency(Number(so.quantity_mt) * Number(so.sale_price_per_mt))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-sm text-gray-500">No sales orders found yet.</div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 flex items-center gap-2">
              <FaTruck className="text-yellow-600" /> Delivery Orders (Dispatches)
            </div>
            <div className="overflow-x-auto">
              {deliveryOrders.length > 0 ? (
                <table className="w-full text-sm text-left whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b">
                    <tr>
                      <th className="px-4 py-2">DO No</th>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Buyer</th>
                      <th className="px-4 py-2">Godown</th>
                      <th className="px-4 py-2 text-right">Qty (MT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deliveryOrders.map((doo, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-semibold text-blue-600">{doo.do_number}</td>
                        <td className="px-4 py-2">{doo.do_date}</td>
                        <td className="px-4 py-2">{doo.buyer_name}</td>
                        <td className="px-4 py-2">{doo.warehouse_name}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatQty(doo.quantity_mt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-sm text-gray-500">No delivery orders generated.</div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-semibold text-gray-700 flex items-center gap-2">
              <FaWarehouse className="text-purple-600" /> Warehouse Flow (Stock Movement)
            </div>
            <div className="overflow-x-auto">
              {warehouseStockFlow.length > 0 ? (
                <table className="w-full text-sm text-left whitespace-nowrap">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase border-b">
                    <tr>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Godown</th>
                      <th className="px-4 py-2 text-right">Qty (MT)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {warehouseStockFlow.map((flow, idx) => {
                      let typeColor = "text-gray-700";
                      if (flow.type === 'Inward') typeColor = "text-blue-600 font-semibold";
                      if (flow.type === 'Outward') typeColor = "text-green-600 font-semibold";
                      if (flow.type === 'Transfer In') typeColor = "text-purple-600";
                      if (flow.type === 'Transfer Out') typeColor = "text-orange-600";

                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2">{new Date(flow.date).toLocaleDateString('en-GB')}</td>
                          <td className={`px-4 py-2 ${typeColor}`}>{flow.type}</td>
                          <td className="px-4 py-2">{flow.warehouse_name}</td>
                          <td className="px-4 py-2 text-right font-medium">{formatQty(flow.quantity)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                 <div className="p-6 text-center text-sm text-gray-500">No warehouse movements recorded.</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MasterReport;
